/**
 * pageDataNormalizer.js - Normalização GENÉRICA de dados de página
 * 
 * Usado para qualquer tipo de página (link-in-bio, clinic, professional)
 * Mantém compatibilidade com estrutura genérica do backend
 * 
 * Config retorna estrutura flat:
 * {
 *   subtitle, scheduleId, socialLinks, genericLinks,
 *   backgroundColor, textColor, fontFamily, fontSize, ...
 * }
 */

/**
 * Garantir que URL começa com https://
 */
const ensureHttps = (url) => {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `https://${trimmed}`;
};

/**
 * Extrair domínio de uma URL
 * Ex: https://www.meusite.com.br → meusite.com.br
 */
const extractDomain = (url) => {
  if (!url) return '';
  try {
    const domain = new URL(ensureHttps(url)).hostname;
    return domain.replace('www.', '');
  } catch (error) {
    console.warn('[pageDataNormalizer] Erro ao extrair domínio:', { url, error: error.message });
    return url;
  }
};

/**
 * Normalizar dados de página antes de enviar para API
 * 
 * @param {object} formData - Dados do formulário (de react-hook-form)
 * @param {string} pageType - Tipo de página (link-in-bio, clinic, professional)
 * @returns {object} Payload GENÉRICO pronto para API
 */
const normalizePageData = (formData, pageType = 'link-in-bio') => {
  // Normalizar social links (remover vazios, garantir https://)
  // Estrutura: apenas URL (sem 'name')
  const normalizedSocialLinks = Object.entries(formData.socialLinks || {})
    .reduce((acc, [key, value]) => {
      const url = value?.url ? ensureHttps(value.url.trim()) : '';
      acc[key] = {
        url: url
      };
      return acc;
    }, {});

  // Normalizar generic links (remover vazios, garantir https://, extrair domínio se sem nome)
  const normalizedGenericLinks = (formData.genericLinks || [])
    .filter(link => link.url?.trim()) // Apenas com URL
    .map(link => {
      const url = ensureHttps(link.url.trim());
      const name = (link.name || '').trim();
      
      return {
        url: url,
        name: name || extractDomain(url) // Se sem nome, extrair domínio
      };
    });

  // Montar config FLAT: todos os campos no mesmo nível
  const flatConfig = {
    logoUrl: formData.logoUrl || null,
    avatarShape: formData.avatarShape || 'round',
    subtitle: (formData.subtitle || '').trim(),
    scheduleId: formData.scheduleId,
    scheduleName: (formData.scheduleName || '').trim(),
    agendaLinkName: (formData.agendaLinkName || '').trim(),
    socialLinks: normalizedSocialLinks,
    genericLinks: normalizedGenericLinks,
    customStyles: {
      backgroundColor: formData.customStyles?.backgroundColor || '',
      textColor: formData.customStyles?.textColor || '',
      fontFamily: formData.customStyles?.fontFamily || '',
      fontSize: formData.customStyles?.fontSize || '',
      linkColor: formData.customStyles?.linkColor || '',
      linkBackground: formData.customStyles?.linkBackground || '',
      cardBackground: formData.customStyles?.cardBackground || ''
    }
  };

  // Retornar payload com config FLAT (compatível com POST /api/pages e PUT /api/pages/{id})
  return {
    type: pageType,
    title: (formData.title || '').trim(),
    slug: (formData.slug || '').trim(),
    templateId: formData.templateId,
    config: flatConfig  // Config flat com todos os campos no mesmo nível
  };
};

/**
 * Normalizar apenas para edição (pode omitir campos imutáveis como slug em algumas APIs)
 */
const normalizePageDataForUpdate = (formData, pageType = 'link-in-bio') => {
  // Mesma normalização, API não diferencia POST vs PUT neste caso
  return normalizePageData(formData, pageType);
};

export {
  normalizePageData,
  normalizePageDataForUpdate,
  ensureHttps,
  extractDomain
};

